import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import accountService from "../services/account.service";
import { AppTokenRequest } from "../types/AppTokenRequest";
import LoadingOverlay from "../components/LoadingOverlay";
import useLocale from "../hooks/useLocale";

const ApiAuthLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const localeCtx = useLocale();
  const [error, setError] = useState<string>();

  const externalToken = searchParams.get("externalToken");

  useEffect(() => {
    if (!externalToken) {
      navigate("/unauthorized", { replace: true });
      return;
    }
    handleApiAuthLogin(externalToken);
  }, []);

  const handleApiAuthLogin = async (token: string) => {
    try {
      const result = await accountService.validateApiAuthToken(token);

      const clientId = result.clientId;
      const redirectUri = result.redirectUri;

      // If MFA required, redirect to MFA screen
      if (result.tempToken || (result.mfaModes && result.mfaModes.length > 0)) {
        const query = new URLSearchParams({
          clientId: clientId,
          redirectUri: redirectUri,
          username: result.username ?? "",
          tempToken: result.tempToken ?? "",
        }).toString();
        navigate(`/mfa?${query}`, { replace: true });
        return;
      }

      // Otherwise, proceed to get App Token (server should have set auth-cookie if baseToken is present)
      const reqPayload: AppTokenRequest = {
        redirectUri: redirectUri,
      };
      const appTokenResponse = await accountService.getAppToken(reqPayload, clientId);

      const redirectUrl = new URL(redirectUri);
      let finalRedirectUri = redirectUri;
      if (redirectUrl.search === "") {
        finalRedirectUri = `${finalRedirectUri}?appToken=${appTokenResponse.token}`;
      } else {
        finalRedirectUri = `${finalRedirectUri}&appToken=${appTokenResponse.token}`;
      }

      finalRedirectUri = `${finalRedirectUri}&username=${encodeURIComponent(
        appTokenResponse.username
      )}&language=${encodeURIComponent(
        localeCtx?.selectedLocale?.current.locale.code ?? "en-GB"
      )}`;

      window.location.replace(finalRedirectUri);
    } catch (err) {
      if (err instanceof AxiosError) {
        const errMessage =
          err.response?.data?.message ?? "Failed to validate API authentication token.";
        setError(errMessage);
      } else {
        setError("Failed to validate API authentication token.");
      }
      return;
    }
  };

  if (error) {
    return (
      <div className="loginFull float-left w-100 h-100">
        <div className="loginBg h-100 p-l-15 p-r-15">
          <div className="row m-b-20 h-100">
            <div className="col-md-12 h-100">
              <div className="formGroup h-100">
                <div className="formInrG cardEffect" style={{ position: "relative" }}>
                  <div className="p-t-15">
                    <p className="text-danger m-t-20">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loginFull float-left w-100 h-100">
      <div className="loginBg h-100 p-l-15 p-r-15">
        <LoadingOverlay
          customStyle={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          themeColor="primary"
          size="large"
          loadingText="Authenticating..."
        />
      </div>
    </div>
  );
};

export default ApiAuthLogin;
